import React, { useEffect, useState } from "react";
import {
  InputFieldSelect,
  InputFieldText,
  FieldGroup,
  Field,
  SubmitButton,
  FormCard,
} from "../../reusable";
import { FormPartInterface, SaveValueInterface } from "../../../interfaces";

export const Medication = (props: FormPartInterface) => {
  const { saveValueFunct, isActive, assignGroup } = props;
  const [currentStep, setCurrentStep] = useState(0);
  const [postCurrentStep, setPostCurrentStep] = useState(0);
  const [stepData, setStepData] = useState<any>({});
  const [showErrorsByStep, setShowErrorsByStep] = useState<{
    [key: number]: boolean;
  }>({});
  const [isFormValid, setIsFormValid] = useState(true);
  const [dateValidationError, setDateValidationError] = useState("");

  // Remove automatic validation - only validate when user tries to proceed

  // Date validation effect
  useEffect(() => {
    validateEndDate();
  }, [
    stepData.medication_start_month,
    stepData.medication_start_year,
    stepData.medication_end_month,
    stepData.medication_end_year,
    stepData.is_still_taking,
  ]);

  const preSaveValueFunct = (dataToSave: SaveValueInterface) => {
    const saved = saveValueFunct(dataToSave);
    setStepData((prevData: any) => ({
      ...prevData,
      [dataToSave.field]: dataToSave.value,
    }));
    return saved;
  };

  const validateEndDate = () => {
    if (
      stepData.is_still_taking === "0" &&
      stepData.medication_start_month &&
      stepData.medication_start_year &&
      stepData.medication_end_month &&
      stepData.medication_end_year
    ) {
      const startDate = new Date(
        parseInt(stepData.medication_start_year),
        parseInt(stepData.medication_start_month) - 1
      );
      const endDate = new Date(
        parseInt(stepData.medication_end_year),
        parseInt(stepData.medication_end_month) - 1
      );

      if (endDate < startDate) {
        setDateValidationError("End date cannot be before start date");
        return false;
      } else {
        setDateValidationError("");
        return true;
      }
    }
    setDateValidationError("");
    return true;
  };

  const validateForm = () => {
    // Validate per visual sub-step
    if (currentStep === 0) {
      return !!(
        stepData.medication && String(stepData.medication).trim() !== ""
      );
    }
    if (currentStep === 1) {
      return !!(
        stepData.usage_reason && String(stepData.usage_reason).trim() !== ""
      );
    }
    if (currentStep === 2) {
      const hasStartMonth = !!stepData.medication_start_month;
      const hasStartYear = !!stepData.medication_start_year;
      return hasStartMonth && hasStartYear;
    }
    if (currentStep === 3) {
      if (!stepData.is_still_taking) return false;
      if (stepData.is_still_taking === "0") {
        const hasEndMonth = !!stepData.medication_end_month;
        const hasEndYear = !!stepData.medication_end_year;
        // rely on effect-populated dateValidationError to avoid state updates in render
        return hasEndMonth && hasEndYear && dateValidationError === "";
      }
      return true;
    }
    if (currentStep === 4) {
      return !!(
        stepData.medication_source &&
        String(stepData.medication_source).trim() !== ""
      );
    }
    return true;
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    } else {
      assignGroup("contact");
    }
  };

  const handleContinue = () => {
    // mark this step to show errors
    setShowErrorsByStep((prev) => ({ ...prev, [currentStep]: true }));
    if (!validateForm()) return;

    // Advance through visual sub-steps
    if (currentStep < 4) {
      setCurrentStep((s) => s + 1);
      // Scroll to top after step change
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // On final sub-step, proceed
    setTimeout(() => assignGroup("adverse"), 400);
  };

  return isActive === "medication" ? (
    <FieldGroup title="Medication Information">
      <FormCard
        assignPostStep={setPostCurrentStep}
        step={currentStep}
        onBack={handleBack}
        isFirstStep={false}
      >
        {currentStep === 0 && (
          <Field
            fieldLabel="Which medication are/were you using?"
            labelPosition="left"
            enhanced={true}
          >
            <InputFieldSelect
              fieldName="medication"
              saveValue={preSaveValueFunct}
              options={[
                {
                  value: "Ozempic (semaglutide)",
                  label: "Ozempic (semaglutide)",
                },
                {
                  value: "Wegovy (semaglutide)",
                  label: "Wegovy (semaglutide)",
                },
                {
                  value: "Rybelsus (oral semaglutide)",
                  label: "Rybelsus (oral semaglutide)",
                },
                {
                  value: "Saxenda (liraglutide)",
                  label: "Saxenda (liraglutide)",
                },
                {
                  value: "Victoza (liraglutide)",
                  label: "Victoza (liraglutide)",
                },
                {
                  value: "Trulicity (dulaglutide)",
                  label: "Trulicity (dulaglutide)",
                },
                {
                  value: "Mounjaro (tirzepatide)",
                  label: "Mounjaro (tirzepatide)",
                },
                {
                  value: "Zepbound (tirzepatide)",
                  label: "Zepbound (tirzepatide)",
                },
                { value: "Not sure", label: "Not sure" },
              ]}
              required={true}
              showErrors={!!showErrorsByStep[0]}
              enhanced={true}
            />
          </Field>
        )}

        {currentStep === 1 && (
          <Field
            fieldLabel="What were you using it for?"
            labelPosition="left"
            enhanced={true}
          >
            <InputFieldSelect
              fieldName="usage_reason"
              saveValue={preSaveValueFunct}
              options={[
                {
                  value: "PCOS (Polycystic Ovary Syndrome)",
                  label: "PCOS (Polycystic Ovary Syndrome)",
                },
                {
                  value: "Insulin resistance (not diabetes-related)",
                  label: "Insulin resistance (not diabetes-related)",
                },
                { value: "Prediabetes", label: "Prediabetes" },
                {
                  value: "Body recomposition / Muscle retention",
                  label: "Body recomposition / Muscle retention",
                },
                {
                  value: "Post-pregnancy weight loss",
                  label: "Post-pregnancy weight loss",
                },
                {
                  value: "Doctor recommendation without formal diagnosis",
                  label: "Doctor recommendation without formal diagnosis",
                },
                {
                  value: "Online weight loss programme",
                  label: "Online weight loss programme",
                },
                {
                  value: "Celebrity or influencer recommendation",
                  label: "Celebrity or influencer recommendation",
                },
                {
                  value: "Purchased online without prescription",
                  label: "Purchased online without prescription",
                },
                {
                  value:
                    "Other aesthetic reason (e.g. event, holiday, photoshoot)",
                  label:
                    "Other aesthetic reason (e.g. event, holiday, photoshoot)",
                },
              ]}
              required={true}
              showErrors={!!showErrorsByStep[1]}
              enhanced={true}
            />
          </Field>
        )}

        {currentStep === 2 && (
          <>
            <h3 style={{ margin: "0 0 10px", color: "#2c3e50" }}>
              When did you start taking your medication?
            </h3>
            <div style={{ display: "flex", gap: "12px" }}>
              <Field
                fieldLabel="Start month"
                labelPosition="left"
                enhanced={true}
                extraStyle={{ flex: 1 }}
              >
                <InputFieldSelect
                  fieldName="medication_start_month"
                  saveValue={preSaveValueFunct}
                  options={[
                    { value: "1", label: "January" },
                    { value: "2", label: "February" },
                    { value: "3", label: "March" },
                    { value: "4", label: "April" },
                    { value: "5", label: "May" },
                    { value: "6", label: "June" },
                    { value: "7", label: "July" },
                    { value: "8", label: "August" },
                    { value: "9", label: "September" },
                    { value: "10", label: "October" },
                    { value: "11", label: "November" },
                    { value: "12", label: "December" },
                  ]}
                  required={true}
                  showErrors={!!showErrorsByStep[2]}
                  enhanced={true}
                />
              </Field>
              <Field
                fieldLabel="Start year"
                labelPosition="left"
                enhanced={true}
                extraStyle={{ flex: 1 }}
              >
                <InputFieldSelect
                  fieldName="medication_start_year"
                  saveValue={preSaveValueFunct}
                  options={Array.from({ length: 30 }, (_, i) => ({
                    value: String(new Date().getFullYear() - i),
                    label: String(new Date().getFullYear() - i),
                  }))}
                  required={true}
                  showErrors={!!showErrorsByStep[2]}
                  enhanced={true}
                />
              </Field>
            </div>
          </>
        )}

        {currentStep === 3 && (
          <>
            <Field
              fieldLabel="Are you still taking it?"
              labelPosition="left"
              enhanced={true}
            >
              <InputFieldSelect
                fieldName="is_still_taking"
                saveValue={preSaveValueFunct}
                options={[
                  { value: "1", label: "Yes" },
                  { value: "0", label: "No" },
                ]}
                required={true}
                showErrors={!!showErrorsByStep[3]}
                enhanced={true}
              />
            </Field>

            {stepData.is_still_taking === "0" && (
              <>
                <div style={{ display: "flex", gap: "12px" }}>
                  <Field
                    fieldLabel="End month"
                    labelPosition="left"
                    enhanced={true}
                    extraStyle={{ flex: 1 }}
                  >
                    <InputFieldSelect
                      fieldName="medication_end_month"
                      saveValue={preSaveValueFunct}
                      options={[
                        { value: "1", label: "January" },
                        { value: "2", label: "February" },
                        { value: "3", label: "March" },
                        { value: "4", label: "April" },
                        { value: "5", label: "May" },
                        { value: "6", label: "June" },
                        { value: "7", label: "July" },
                        { value: "8", label: "August" },
                        { value: "9", label: "September" },
                        { value: "10", label: "October" },
                        { value: "11", label: "November" },
                        { value: "12", label: "December" },
                      ]}
                      required={true}
                      showErrors={!!showErrorsByStep[3]}
                      enhanced={true}
                    />
                  </Field>
                  <Field
                    fieldLabel="End year"
                    labelPosition="left"
                    enhanced={true}
                    extraStyle={{ flex: 1 }}
                  >
                    <InputFieldSelect
                      fieldName="medication_end_year"
                      saveValue={preSaveValueFunct}
                      options={Array.from({ length: 30 }, (_, i) => ({
                        value: String(new Date().getFullYear() - i),
                        label: String(new Date().getFullYear() - i),
                      }))}
                      required={true}
                      showErrors={!!showErrorsByStep[3]}
                      enhanced={true}
                    />
                  </Field>
                </div>
                <div
                  style={{
                    color: "#dc2626",
                    fontSize: "14px",
                    marginTop: "8px",
                    padding: "8px 12px",
                    backgroundColor: "#fef2f2",
                    border: "1px solid #dc2626",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minHeight: "40px",
                    opacity: dateValidationError ? 1 : 0,
                    pointerEvents: dateValidationError ? "auto" : "none",
                    transition: "opacity 0.2s ease",
                  }}
                >
                  <span style={{ fontSize: "16px" }}>⚠️</span>
                  {dateValidationError || ""}
                </div>
              </>
            )}
          </>
        )}

        {currentStep === 4 && (
          <Field
            fieldLabel="Where did you obtain the medication?"
            labelPosition="left"
            enhanced={true}
          >
            <InputFieldSelect
              fieldName="medication_source"
              saveValue={preSaveValueFunct}
              options={[
                { value: "GP/NHS", label: "GP/NHS" },
                { value: "Private clinic", label: "Private clinic" },
                { value: "Online pharmacy", label: "Online pharmacy" },
                { value: "Other", label: "Other" },
              ]}
              required={true}
              showErrors={!!showErrorsByStep[4]}
              enhanced={true}
            />
          </Field>
        )}

        <SubmitButton
          text="Continue"
          btnType="primary"
          onClick={handleContinue}
          disabled={
            !validateForm() || (currentStep === 3 && !!dateValidationError)
          }
        />
      </FormCard>
    </FieldGroup>
  ) : (
    <></>
  );
};
